/**
 * One place this account is signed in — a row of "Where you're signed in":
 * browser and device, where and when, "This device" for the current one and
 * Sign out for the others. Data: `useActiveSessions` (Clerk's `getSessions`).
 */
import { useState } from 'react';
import { useTranslation } from '@tonnie/locale';
import { Button, SettingsBadge, SettingsRow } from '@tonnie/ui';
import { Loader2, Monitor, Smartphone } from '@tonnie/assets';

import type { ActiveSession } from './hooks/use-active-sessions';

interface SessionRowProps {
  session: ActiveSession;
  onRevoke: (sessionId: string) => Promise<void>;
}

export function SessionRow({ session, onRevoke }: SessionRowProps) {
  const { t } = useTranslation();
  const [isRevoking, setIsRevoking] = useState(false);
  const DeviceIcon = session.isMobile ? Smartphone : Monitor;

  const handleRevoke = async () => {
    setIsRevoking(true);
    await onRevoke(session.id);
    setIsRevoking(false);
  };

  return (
    <SettingsRow
      icon={<DeviceIcon className="h-[18px] w-[18px]" />}
      title={t('profile.sessionBrowserOnDevice', {
        browser: session.browser,
        device: session.device,
      })}
      badge={
        session.isCurrent ? (
          <SettingsBadge tone="success">{t('auth.security.sessions.thisDevice')}</SettingsBadge>
        ) : undefined
      }
      detail={`${session.location} · ${session.isCurrent ? t('profile.currentSession') : session.lastActive}`}
      action={
        session.isCurrent ? undefined : (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive-accessible hover:bg-destructive/10 hover:text-destructive-accessible"
            onClick={() => void handleRevoke()}
            disabled={isRevoking}
            data-testid={`settings-session-revoke-${session.id}`}
          >
            {isRevoking && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
            {t('auth.security.sessions.signOut')}
          </Button>
        )
      }
      data-testid={`settings-session-${session.id}`}
    />
  );
}
